import { ArrowLeft, Save, Upload, Cloud } from 'lucide-solid'
import {
  Component,
  Show,
  createSignal,
  onMount,
  createEffect,
} from 'solid-js'
import { useNavigate } from '@solidjs/router'
import AvatarIcon from '../../shared/AvatarIcon'
import Button from '../../shared/Button'
import FileInput, { FileInputResult } from '../../shared/FileInput'
import TextInput from '../../shared/TextInput'
import { userStore, toastStore } from '../../store'
import { Page } from '/web/Layout'
import { getAssetUrl } from '../../shared/util'

const ProfileSettings: Component = () => {
  const nav = useNavigate()
  const state = userStore((s) => ({ profile: s.profile, user: s.user }))

  const [handle, setHandle] = createSignal(state.profile?.handle || '')
  const [bio, setBio] = createSignal(state.profile?.bio || '')
  const [avatar, setAvatar] = createSignal<File | undefined>()
  const [avatarPreview, setAvatarPreview] = createSignal<string | undefined>()
  const [oldPassword, setOldPassword] = createSignal('')
  const [newPassword, setNewPassword] = createSignal('')
  const [confirmPassword, setConfirmPassword] = createSignal('')
  const [isSaving, setIsSaving] = createSignal(false)

  onMount(() => {
    userStore.getProfile()
    userStore.getConfig()
  })

  createEffect(() => {
    if (state.profile?.handle) {
      setHandle(state.profile.handle)
    }
    if (state.profile?.bio) {
      setBio(state.profile.bio)
    }
  })

  const onAvatar = (files: FileInputResult[]) => {
    const [file] = files
    if (!file) {
      setAvatar(undefined)
      setAvatarPreview(undefined)
      return
    }
    setAvatar(() => file.file)
    
    // Create preview
    const reader = new FileReader()
    reader.onload = (e) => {
      setAvatarPreview(e.target?.result as string)
    }
    reader.readAsDataURL(file.file)
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await userStore.updateProfile({
        handle: handle(),
        avatar: avatar(),
        bio: bio(),
      })
      toastStore.success('个人资料已保存')
      nav('/profile')
    } catch (error: any) {
      toastStore.error(`保存失败: ${error?.message || error}`)
    } finally {
      setIsSaving(false)
    }
  }

  const handleChangePassword = async () => {
    if (!newPassword() || !confirmPassword()) {
      toastStore.warn('请填写新密码和确认密码')
      return
    }

    if (newPassword() !== confirmPassword()) {
      toastStore.warn('新密码和确认密码不匹配')
      return
    }

    if (newPassword().length < 6) {
      toastStore.warn('密码长度至少6位')
      return
    }

    try {
      await userStore.changePassword(newPassword())
      toastStore.success('密码修改成功')
      setOldPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (error: any) {
      toastStore.error(`密码修改失败: ${error?.message || error}`)
    }
  }

  return (
    <Page>
      <div class="flex flex-col min-h-[80vh] px-4 py-6">
        <div class="w-full max-w-2xl mx-auto">
          {/* Header */}
          <div class="flex items-center mb-6">
            <Button
              schema="clear"
              onClick={() => nav('/profile')}
              class="mr-4"
            >
              <ArrowLeft size={20} />
            </Button>
            <h1 class="text-2xl font-bold text-white">个人设置</h1>
          </div>

          <div class="bg-[var(--bg-800)] rounded-lg border border-[var(--hl-500)] p-6 space-y-6">
            {/* Avatar Upload Section */}
            <div>
              <label class="block text-sm font-semibold mb-2 bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
                头像
              </label>
              <div class="border-2 border-dashed border-[var(--hl-500)] rounded-lg p-8 text-center bg-[var(--bg-700)]">
                <Show
                  when={avatarPreview() || state.profile?.avatar}
                  fallback={
                    <>
                      <Cloud size={48} class="mx-auto mb-4 text-[var(--text-500)]" />
                      <p class="text-[var(--text-500)] mb-2">点击或拖拽上传图片</p>
                      <p class="text-xs text-[var(--text-600)]">最大 5 MB</p>
                    </>
                  }
                >
                  <div class="flex justify-center mb-4">
                    <div class="relative">
                      <img
                        src={avatarPreview() || (state.profile?.avatar ? getAssetUrl(state.profile.avatar) : '')}
                        alt="Avatar preview"
                        class="w-32 h-32 rounded-full object-cover border-2 border-[var(--hl-500)]"
                      />
                    </div>
                  </div>
                  <p class="text-[var(--text-500)] mb-2">点击或拖拽上传图片</p>
                  <p class="text-xs text-[var(--text-600)]">最大 5 MB</p>
                </Show>
                <FileInput
                  fieldName="avatar"
                  accept="image/jpeg,image/png,image/jpg"
                  onUpdate={onAvatar}
                  class="mt-4"
                />
              </div>
            </div>

            {/* Nickname Section */}
            <div>
              <label class="block text-sm font-semibold mb-2 bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
                昵称
              </label>
              <p class="text-sm text-[var(--text-500)] mb-2">你在平台显示的名字</p>
              <TextInput
                fieldName="handle"
                value={handle()}
                onChange={(ev) => setHandle(ev.currentTarget.value)}
                placeholder="输入昵称"
              />
            </div>

            {/* Bio Section */}
            <div>
              <label class="block text-sm font-semibold mb-2 bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
                个人简介
              </label>
              <p class="text-sm text-[var(--text-500)] mb-2">介绍一下自己,让大家更了解你</p>
              <textarea
                value={bio()}
                onInput={(ev) => setBio(ev.currentTarget.value)}
                placeholder="个人简介"
                maxLength={200}
                class="w-full h-32 px-4 py-3 bg-[var(--bg-700)] border border-[var(--bg-600)] rounded-lg text-white placeholder-[var(--text-600)] resize-none focus:outline-none focus:border-[var(--hl-500)]"
              />
              <div class="text-right text-xs text-[var(--text-500)] mt-1">
                {bio().length}/200
              </div>
            </div>

            {/* Password Change Section */}
            <div>
              <label class="block text-sm font-semibold mb-2 bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
                修改密码
              </label>
              <div class="space-y-3">
                <TextInput
                  type="password"
                  fieldName="oldPassword"
                  placeholder="当前密码（可选）"
                  value={oldPassword()}
                  onChange={(ev) => setOldPassword(ev.currentTarget.value)}
                />
                <TextInput
                  type="password"
                  fieldName="newPassword"
                  placeholder="新密码"
                  value={newPassword()}
                  onChange={(ev) => setNewPassword(ev.currentTarget.value)}
                />
                <TextInput
                  type="password"
                  fieldName="confirmPassword"
                  placeholder="确认新密码"
                  value={confirmPassword()}
                  onChange={(ev) => setConfirmPassword(ev.currentTarget.value)}
                />
                <Button
                  schema="secondary"
                  onClick={handleChangePassword}
                  disabled={!newPassword() || !confirmPassword()}
                  class="w-full"
                >
                  修改密码
                </Button>
              </div>
            </div>

            {/* Save Button */}
            <div class="pt-4">
              <Button
                schema="primary"
                onClick={handleSave}
                disabled={isSaving()}
                class="w-full rounded-lg py-3 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 hover:from-blue-600 hover:via-purple-600 hover:to-pink-600"
              >
                <Save size={18} class="mr-2" />
                {isSaving() ? '保存中...' : '保存'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Page>
  )
}

export default ProfileSettings

